import * as R from 'ramda'
import { computed, ComputedRef, onUnmounted, Ref, ref, toRaw } from 'vue'

import {
  AddIncident,
  isAddIncident,
  OnChangePayload,
  QueueIncident,
} from '@collector/desktop-feature-incidents-queue-core'
import { VolleyballIncidentId } from '@collector/sportsapi-client-legacy'
import { injectEventIncidentsQueue } from '@desktop/views/Relation/provide-inject'

import { isReceptionHandler, isServeHandler } from './utils'

export function useServeQueueIncident(): {
  lastServeQueueIncident: Ref<AddIncident | null>
  /** queue incidents */
  isLastServeQueueIncidentHandled: ComputedRef<boolean>
  isLastReceptionQueueIncidentHandled: ComputedRef<boolean>
  /** history incidents */
  isLastServeIncidentHandled: ComputedRef<boolean>
  isLastReceptionIncidentHandled: ComputedRef<boolean>
} {
  // inject
  const eventIncidentsQueue = injectEventIncidentsQueue()

  // state
  const lastServeQueueIncident = ref<AddIncident | null>(null)
  const queue = ref<QueueIncident[]>(eventIncidentsQueue.queue)

  // computed
  const isLastServeQueueIncidentHandled = computed(() =>
    isLastServeQueueIncidentsHandled(isServeHandler),
  )

  const isLastReceptionQueueIncidentHandled = computed(() =>
    isLastServeQueueIncidentsHandled(isReceptionHandler),
  )

  const isLastServeIncidentHandled = computed(() =>
    isLastServeIncidentsHandled(isServeHandler),
  )

  const isLastReceptionIncidentHandled = computed(() =>
    isLastServeIncidentsHandled(isReceptionHandler),
  )

  // methods
  function isLastServeIncidentsHandled(
    isIncidentIdHandler: (incidentId: VolleyballIncidentId) => boolean,
  ): boolean {
    if (lastServeQueueIncident.value) {
      return isLastServeQueueIncidentsHandled(isIncidentIdHandler)
    }

    for (const queueIncident of queue.value) {
      // check if any incident finished current Serve sequence in history
      if (isAddIncident(queueIncident)) {
        if (queueIncident.payload.incident_id === VolleyballIncidentId.Serve) {
          return false
        } else if (
          isIncidentIdHandler(
            queueIncident.payload.incident_id as VolleyballIncidentId,
          )
        ) {
          return true
        }
      }
    }

    return false
  }

  function isLastServeQueueIncidentsHandled(
    isIncidentIdHandler: (incidentId: VolleyballIncidentId) => boolean,
  ): boolean {
    if (!lastServeQueueIncident.value) {
      return true
    }

    const lastServeIncidentIndex = queue.value.indexOf(
      toRaw(lastServeQueueIncident.value),
    )

    for (let i = lastServeIncidentIndex + 1; i < queue.value.length; i++) {
      const queueIncident = queue.value[i]

      // check if any incident finished current Serve sequence on queue
      if (
        isAddIncident(queueIncident) &&
        isIncidentIdHandler(
          queueIncident.payload.incident_id as VolleyballIncidentId,
        )
      ) {
        return true
      }
    }

    return false
  }

  function setLastServeIncident(queue: QueueIncident[]): void {
    const queueAddIncident = R.findLast(
      (i) =>
        isAddIncident(i) &&
        i.payload.incident_id === VolleyballIncidentId.Serve,
      queue,
    )

    if (queueAddIncident) {
      lastServeQueueIncident.value = queueAddIncident as AddIncident
    } else {
      lastServeQueueIncident.value = null
    }
  }

  function onQueueChange(change: OnChangePayload): void {
    setLastServeIncident(change.queue)
    queue.value = [...change.queue]
  }

  // init
  eventIncidentsQueue.subscribeOnChange(onQueueChange)

  onUnmounted(() => {
    eventIncidentsQueue.unsubscribeOnChange(onQueueChange)
  })

  setLastServeIncident(eventIncidentsQueue.queue)

  return {
    lastServeQueueIncident,
    isLastServeQueueIncidentHandled,
    isLastReceptionQueueIncidentHandled,
    isLastServeIncidentHandled,
    isLastReceptionIncidentHandled,
  }
}
